import { useMemo, useState, useCallback } from 'react'
import { arrayMove } from '@dnd-kit/sortable'
import { STUDENTS_COLUMNS } from '../../../utility/members'
import { useTranslated } from '../../../i18n'
import { translateColumns } from '../../../utility/i18nConverters'

const DEFAULT_ORDER = STUDENTS_COLUMNS.map(c => c.field)

const useColumnManagement = () => {
  const columns = useTranslated(STUDENTS_COLUMNS, translateColumns)
  const [columnOrder, setColumnOrder] = useState(DEFAULT_ORDER)
  const [hiddenColumns, setHiddenColumns] = useState([])

  const visibleColumns = useMemo(
    () => columnOrder
      .map(field => columns.find(c => c.field === field))
      .filter(col => col && !hiddenColumns.includes(col.field)),
    [columns, columnOrder, hiddenColumns]
  )

  const allColumns = useMemo(
    () => columnOrder.map(field => columns.find(c => c.field === field)).filter(Boolean),
    [columns, columnOrder]
  )

  const toggleColumn = useCallback((field) => {
    setHiddenColumns(prev =>
      prev.includes(field) ? prev.filter(f => f !== field) : [...prev, field]
    )
  }, [])

  const reorderColumns = useCallback((activeField, overField) => {
    setColumnOrder(prev => arrayMove(prev, prev.indexOf(activeField), prev.indexOf(overField)))
  }, [])

  const resetToDefaults = useCallback(() => {
    setColumnOrder(DEFAULT_ORDER)
    setHiddenColumns([])
  }, [])

  const isDefaultOrder = JSON.stringify(columnOrder) === JSON.stringify(DEFAULT_ORDER) && hiddenColumns.length === 0

  return {
    visibleColumns,
    allColumns,
    hiddenColumns,
    isDefaultOrder,
    toggleColumn,
    reorderColumns,
    resetToDefaults,
  }
}

export default useColumnManagement
